import { useRef, useEffect, useState, useCallback } from 'react';

import Pusher from 'pusher-js';

export const ConnectionState = {
  CONNECTING: 'connecting',
  CONNECTED: 'connected',
  ERROR: 'error'
};

export function usePusherChannel({ key, cluster, channel: channelName, channelAuthUrl }) {
  const pusherChannelRef = useRef(null);
  const [connectionStatus, setConnectionStatus] = useState(ConnectionState.CONNECTING);
  const prevConnectionStatusRef = useRef(ConnectionState.CONNECTING);
  const [error, setError] = useState(null);
  const onStatusChangeSubscribers = useRef([]);

  const addOnStatusChangeSubscriber = useCallback((fn) => {
    onStatusChangeSubscribers.current.push(fn)
  }, [onStatusChangeSubscribers]);
  const handleError = (pusherError) => {
    setConnectionStatus(ConnectionState.ERROR);
    setError(pusherError);
  };

  useEffect(() => {
    const pusher = new Pusher(key, {
      cluster: cluster,
      channelAuthorization: {
        endpoint: channelAuthUrl,
        headersProvider: () => {
          return {
            "X-CSRF-Token": document.querySelector('[name="csrf-token"]').content,
          };
        },
      },
    });

    // https://pusher.com/docs/channels/using_channels/connection/#available-states
    pusher.connection.bind('connecting', () => { setConnectionStatus(ConnectionState.CONNECTING)});
    pusher.connection.bind('connected', () => { setConnectionStatus(ConnectionState.CONNECTED)});
    pusher.connection.bind('unavailable', () => handleError());
    pusher.connection.bind('error', (generalError) => handleError(generalError));

    pusherChannelRef.current = pusher.subscribe(channelName);
    pusherChannelRef.current.bind('pusher:subscription_error', subscriptionError => {
      handleError(subscriptionError);
    });

    return () => pusher.unsubscribe(channelName);
  }, [key, cluster, channelName]);

  useEffect(() => {
    onStatusChangeSubscribers.current.forEach(fn => fn(connectionStatus, prevConnectionStatusRef.current));
    prevConnectionStatusRef.current = connectionStatus
  }, [connectionStatus]);

  return {
    channel: pusherChannelRef.current,
    connectionStatus,
    error,
    addOnStatusChangeSubscriber
  }
}
